import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import LabelArt from "@/components/LabelArt";

const CartDrawer = () => {
  const { isOpen, closeCart, items, productsBySlug, setQty, remove, subtotal, checkout } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try { await checkout(); } catch { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div data-testid="cart-drawer-overlay" className="fixed inset-0 z-[70] bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeCart} />
          <motion.aside data-testid="cart-drawer" className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-ivory border-l border-line"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 260 }}>
            <div className="flex items-center justify-between border-b border-line px-8 py-6">
              <h2 className="font-serif text-2xl font-light tracking-tight">Your Bag</h2>
              <button data-testid="cart-drawer-close" onClick={closeCart} className="p-1 hover:opacity-60 transition-opacity" aria-label="Close cart"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-6 text-center">
                  <p className="font-serif text-xl font-light text-stone2 italic">Your bag is empty.</p>
                  <Link to="/shop" onClick={closeCart} data-testid="cart-empty-shop-link" className="btn-outline">Shop Candles</Link>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((item) => {
                    const p = productsBySlug[item.slug];
                    if (!p) return null;
                    return (
                      <li key={item.slug} data-testid={`cart-item-${item.slug}`} className="flex gap-5 border-b border-line pb-6">
                        <div className="h-24 w-20 shrink-0 overflow-hidden border border-line bg-white">
                          {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <LabelArt name={p.name} size={p.size} compact />}
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-serif text-lg font-light leading-tight">{p.name}</p>
                              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone2">{p.size} · Soy Wax</p>
                            </div>
                            <button data-testid={`cart-remove-${item.slug}`} onClick={() => remove(item.slug)} className="text-stone2 hover:text-ink transition-colors" aria-label="Remove"><X size={15} /></button>
                          </div>
                          <div className="mt-auto flex items-center justify-between pt-3">
                            <div className="flex items-center border border-line">
                              <button data-testid={`cart-qty-minus-${item.slug}`} onClick={() => setQty(item.slug, item.qty - 1)} className="px-2.5 py-1.5 hover:bg-cream transition-colors"><Minus size={12} /></button>
                              <span className="w-8 text-center text-xs" data-testid={`cart-qty-${item.slug}`}>{item.qty}</span>
                              <button data-testid={`cart-qty-plus-${item.slug}`} onClick={() => setQty(item.slug, item.qty + 1)} className="px-2.5 py-1.5 hover:bg-cream transition-colors"><Plus size={12} /></button>
                            </div>
                            <p className="text-sm font-medium">${(p.price * item.qty).toFixed(2)}</p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-line px-8 py-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl font-light">${subtotal.toFixed(2)}</span>
                </div>
                {subtotal >= 75 ? (
                  <p data-testid="free-shipping-unlocked" className="mt-2 text-xs text-ink">Free shipping unlocked — your order ships on us.</p>
                ) : (
                  <div className="mt-3" data-testid="free-shipping-progress">
                    <p className="text-xs text-stone2">You're <span className="text-ink font-medium">${(75 - subtotal).toFixed(2)}</span> away from free shipping</p>
                    <div className="mt-2 h-[3px] w-full bg-line overflow-hidden">
                      <div className="h-full bg-ink transition-all duration-700 ease-out" style={{ width: `${Math.min(100, (subtotal / 75) * 100)}%` }} />
                    </div>
                  </div>
                )}
                <p className="mt-2 text-xs text-stone2">{subtotal >= 75 ? "Taxes" : "Flat $6.95 standard shipping and taxes"} calculated at secure checkout.</p>
                <button data-testid="cart-checkout-button" onClick={handleCheckout} disabled={loading} className="btn-primary mt-5 w-full disabled:opacity-50">
                  {loading ? "Preparing Checkout…" : "Checkout"}
                </button>
                <Link to="/shop" onClick={closeCart} data-testid="cart-continue-shopping" className="mt-3 block text-center text-[10px] uppercase tracking-[0.28em] text-stone2 hover:text-ink transition-colors py-2">
                  Continue Shopping
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
